/* akk-collection.js: filters, sort, paging and the return trip on the collection page.
   Progressive enhancement over one GET form. Every control is a plain link or
   submit without this script; with it, results swap in place through the
   Section Rendering API and the next page appends below the grid.

   Collection polish C0 to C2 (2026-11-01, docs/research/reports/Akkúrat collection page polish.md):
   - the page remembers its place: ?page=N stays in the URL as pages append, a landing on
     ?page=N fetches pages 1..N-1 and prepends them with scroll compensation, and Back from a
     product (navigation type back_forward) scrolls to the card that was clicked;
   - appended and prepended cards take the grid's own image box, so one row never holds two heights;
   - the empty nav slot goes whenever no related row renders;
   - focus survives a swap (refocus by id, chip index or the submit), one fetch per action
     (price fields debounced, a submit that equals the fetch in flight is skipped), a view
     transition cross-fades the grid where the device allows it;
   - the automatic second page runs only at 720px and above and never under Save-Data; the
     button carries a busy label; the count announces an append; the last page ends with a line;
   - the phone toolbar returns on scroll-up (html.akk-bar-up / akk-bar-down);
   - card photos fade in from the page colour once loaded (is-loading). */

(() => {
  'use strict';
  if (window.__akkCollectionLoaded) return;
  window.__akkCollectionLoaded = true;

  const html = document.documentElement;
  const SWAP = ['[data-akk-results]', '[data-akk-count]', '[data-akk-active-count]', '[data-akk-drawer-body]', '[data-akk-drawer-foot]'];

  const rootOf = (el) => el.closest('[data-akk-collection]') || document.querySelector('[data-akk-collection]');
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const instant = { behavior: 'instant' };

  const sectionUrl = (root, url) => {
    const u = new URL(url, location.href);
    u.searchParams.set('section_id', root.dataset.akkSection);
    return u.toString();
  };

  const formUrl = (form) => {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    const u = new URL(form.action, location.href);
    u.search = params.toString();
    return u.toString();
  };

  const pageOf = (url) => {
    const n = parseInt(new URL(url, location.href).searchParams.get('page') || '1', 10);
    return n > 0 ? n : 1;
  };
  const withPage = (url, n) => {
    const u = new URL(url, location.href);
    if (n > 1) u.searchParams.set('page', String(n)); else u.searchParams.delete('page');
    return u.toString();
  };
  const key = (li) => { const a = li.querySelector('.akk-card__title'); return a ? a.getAttribute('href').split('?')[0] : null; };
  const handleOf = (href) => (String(href).match(/\/products\/([^/?#]+)/) || [])[1] || null;

  /* --- photos fade in from the page colour once they have arrived (C1, rank 11) --- */
  const markImages = (scope) => {
    scope.querySelectorAll('.akk-card__media img:not(.akk-card__alt)').forEach((img) => {
      if (img.complete || img.classList.contains('is-loading')) return;
      img.classList.add('is-loading');
      const done = () => img.classList.remove('is-loading');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };

  /* --- the grid's image box: incoming cards take the box the grid already has (C0, rank 2) --- */
  const ratioOf = (grid) => { const m = grid.querySelector('.akk-card__media'); return m ? m.style.aspectRatio : ''; };
  const applyRatio = (nodes, ratio) => {
    if (!ratio) return;
    nodes.forEach((li) => li.querySelectorAll('.akk-card__media').forEach((m) => { m.style.aspectRatio = ratio; }));
  };

  /* --- focus memory across a swap (C0, rank 4): the control by id, else the chip at the same index, else the submit --- */
  const remember = (root) => {
    const a = document.activeElement;
    if (!a || a === document.body) return null;
    if (!SWAP.some((s) => a.closest(s))) return null;
    const chips = Array.from(root.querySelectorAll('[data-akk-chip]'));
    return { id: a.id || null, chip: chips.indexOf(a.closest('[data-akk-chip]')), foot: !!a.closest('[data-akk-drawer-foot]') };
  };
  const refocus = (root, mem) => {
    if (!mem) return;
    let el = mem.id ? document.getElementById(mem.id) : null;
    if (el && el.disabled) el = null;
    if (!el && mem.chip >= 0) {
      const chips = root.querySelectorAll('[data-akk-chip]');
      el = chips[Math.min(mem.chip, chips.length - 1)] || root.querySelector('[data-akk-filter-open]');
    }
    if (!el && mem.foot) el = root.querySelector('.akk-fdrawer__submit');
    if (el) el.focus({ preventScroll: true });
  };

  const swap = (root, doc) => {
    SWAP.forEach((selector) => {
      const current = root.querySelector(selector) || document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    });
    const chips = root.querySelector('[data-akk-chips]');
    if (chips) chips.hidden = !chips.querySelector('[data-akk-chip]');
    markImages(root);
  };

  // Horizon's guards for a view transition: not behind an open dialog, not under reduced motion, not on a small device.
  const canTransition = () => typeof document.startViewTransition === 'function'
    && !document.querySelector('dialog.akk-fdrawer[open]')
    && !matchMedia('(prefers-reduced-motion: reduce)').matches
    && (navigator.hardwareConcurrency || 4) > 2
    && (navigator.deviceMemory || 4) > 2;

  const swapIn = (root, doc) => {
    const mem = remember(root);
    const run = () => { swap(root, doc); refocus(root, mem); };
    if (!canTransition()) { run(); return Promise.resolve(); }
    return document.startViewTransition(run).finished.catch(() => {});
  };

  /* --- one fetch per action --- */
  let inflight = null;
  let pending = null;
  const load = (root, url) => {
    if (pending) { clearTimeout(pending); pending = null; }
    if (inflight && inflight.url === url) return;
    if (inflight) inflight.controller.abort();
    const controller = new AbortController();
    inflight = { url, controller };
    root.setAttribute('aria-busy', 'true');
    fetch(sectionUrl(root, url), { headers: { Accept: 'text/html' }, signal: controller.signal })
      .then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); })
      .then((text) => swapIn(root, parse(text)))
      .then(() => {
        history.replaceState({}, '', url);
        root.removeAttribute('aria-busy');
        inflight = null;
        arm(root);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        inflight = null;
        location.href = url;
      });
  };

  /* --- paging --- */
  // The automatic second page is a desktop convenience (C0, rank 5): every measured peer makes
  // the phone tap a button, and the unasked page cost 4.2MB there; Save-Data turns it off too.
  const AUTO_MAX = 100;
  const autoAllowed = () => matchMedia('(min-width: 720px)').matches && !(navigator.connection && navigator.connection.saveData);
  let autoBusy = false;
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || autoBusy) return;
          const more = entry.target;
          io.unobserve(more);
          const grid = more.isConnected && rootOf(more).querySelector('[data-akk-grid]');
          if (!grid || grid.children.length >= AUTO_MAX) return;
          autoBusy = true;
          append(rootOf(more), more, true);
        });
      }, { rootMargin: '900px 0px' })
    : null;

  const arm = (root) => {
    if (!io || !autoAllowed()) return;
    const pager = root.querySelector('[data-akk-pager][data-akk-auto]');
    const more = pager && pager.querySelector('[data-akk-more]');
    if (more) io.observe(more);
  };

  const announce = (root) => {
    const status = root.querySelector('[data-akk-status]');
    const tpl = root.dataset.akkAnnounce;
    const shown = root.querySelector('.akk-coll__shown');
    if (!status || !tpl || !shown) return;
    const m = shown.textContent.match(/(\d+)\D+(\d+)\s*$/);
    if (!m) return;
    status.textContent = '';
    requestAnimationFrame(() => { status.textContent = tpl.replace('[sýnt]', m[1]).replace('[fjöldi]', m[2]); });
  };

  const append = (root, more, auto) => {
    // One fetch at a time per button: the observer fires when the button scrolls
    // into view and a click in the same instant fired a second fetch of the same
    // page, so the grid held page 2 twice (found 2026-10-28, both themes).
    if (more.getAttribute('aria-busy') === 'true') return;
    const url = more.href;
    const label = more.textContent;
    more.setAttribute('aria-busy', 'true');
    if (root.dataset.akkBusyLabel) more.textContent = root.dataset.akkBusyLabel;
    fetch(sectionUrl(root, url), { headers: { Accept: 'text/html' } })
      .then((res) => { if (!res.ok) throw new Error(res.status); return res.text(); })
      .then((text) => {
        const doc = parse(text);
        const grid = root.querySelector('[data-akk-grid]');
        const nextGrid = doc.querySelector('[data-akk-grid]');
        if (grid && nextGrid) {
          // never the same product twice, whatever the server paginated while prices or stock changed under us
          const ratio = ratioOf(grid);
          const held = new Set(Array.from(grid.children).map(key).filter(Boolean));
          const incoming = [];
          Array.from(nextGrid.children).forEach((li) => { const k = key(li); if (k && held.has(k)) return; if (k) held.add(k); incoming.push(li); });
          applyRatio(incoming, ratio);
          incoming.forEach((li) => grid.appendChild(li));
          markImages(grid);
        }

        const pager = root.querySelector('[data-akk-pager]');
        const nextPager = doc.querySelector('[data-akk-pager]');
        if (nextPager) {
          const prev = nextPager.querySelector('[rel="prev"]');
          if (prev) prev.remove();
          // The fetched page counts its own range ("101–150 af N"); after an
          // append the grid starts where this page started.
          const start = pager && (pager.querySelector('.akk-coll__shown')?.textContent.match(/^(\d+)–/) || [])[1];
          const shown = nextPager.querySelector('.akk-coll__shown');
          if (shown) {
            const rest = shown.textContent.replace(/^\d+–/, '');
            shown.textContent = start ? `${start}–${rest}` : rest;
          }
        }
        if (pager) {
          if (nextPager) pager.replaceWith(nextPager); else pager.remove();
        }
        history.replaceState({}, '', url);
        announce(root);

        const focusTarget = (nextPager && nextPager.querySelector('[data-akk-more]')) || (nextPager && nextPager.querySelector('.akk-coll__shown'));
        autoBusy = false;
        arm(root);
        if (!auto && focusTarget) {
          if (!focusTarget.hasAttribute('tabindex') && focusTarget.tagName !== 'A') focusTarget.setAttribute('tabindex', '-1');
          focusTarget.focus({ preventScroll: true });
        }
      })
      .catch(() => {
        autoBusy = false;
        if (!auto) { location.href = url; return; }
        more.removeAttribute('aria-busy');
        more.textContent = label;
      });
  };

  /* --- a landing on ?page=N shows pages 1..N-1 above it (C0, rank 1) --- */
  const prependEarlier = async (root) => {
    const n = pageOf(location.href);
    if (n < 2) return;
    const grid = root.querySelector('[data-akk-grid]');
    if (!grid) return;
    const pages = [];
    for (let k = 1; k < n; k++) pages.push(k);
    if (pages.length > 6) return; // a deep landing keeps "Sýna fyrri"
    const docs = [];
    for (let i = 0; i < pages.length; i += 3) { // three at a time: bursts answer 429
      const batch = pages.slice(i, i + 3).map((k) => fetch(sectionUrl(root, withPage(location.href, k)), { headers: { Accept: 'text/html' } })
        .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
        .then(parse));
      docs.push(...(await Promise.all(batch)));
    }
    const anchor = grid.firstElementChild;
    const before = anchor ? anchor.getBoundingClientRect().top : 0;
    const ratio = ratioOf(grid);
    const held = new Set(Array.from(grid.children).map(key).filter(Boolean));
    const frag = document.createDocumentFragment();
    docs.forEach((doc, i) => {
      if (i === 0) {
        // reference mode renders the boxes group on page 1 only; bring it along
        const groups = doc.querySelector('[data-akk-groups]');
        if (groups && !root.querySelector('[data-akk-groups]')) { grid.parentNode.insertBefore(groups, grid); markImages(groups); }
      }
      const g = doc.querySelector('[data-akk-grid]');
      if (!g) return;
      Array.from(g.children).forEach((li) => { const k = key(li); if (k && held.has(k)) return; if (k) held.add(k); frag.appendChild(li); });
    });
    applyRatio(Array.from(frag.children), ratio);
    grid.prepend(frag);
    markImages(grid);
    if (anchor) {
      const after = anchor.getBoundingClientRect().top;
      if (after !== before) window.scrollBy({ top: after - before, left: 0, ...instant });
    }
    const pager = root.querySelector('[data-akk-pager]');
    if (pager) {
      const prev = pager.querySelector('[rel="prev"]');
      if (prev) prev.remove();
      const shown = pager.querySelector('.akk-coll__shown');
      if (shown) shown.textContent = shown.textContent.replace(/^\d+–/, '');
    }
  };

  /* --- Back returns to the card that was clicked (C0, rank 1) --- */
  const snapKey = () => 'akk:coll:' + location.pathname;
  const readSnap = () => { try { return JSON.parse(sessionStorage.getItem(snapKey()) || 'null'); } catch (e) { return null; } };
  const writeSnap = (data) => { try { sessionStorage.setItem(snapKey(), JSON.stringify(data)); } catch (e) {} };
  let lastHandle = null;
  document.addEventListener('click', (event) => {
    const a = event.target.closest('[data-akk-results] a[href*="/products/"]');
    if (a) lastHandle = handleOf(a.href);
  }, true);
  window.addEventListener('pagehide', () => {
    writeSnap({ search: location.search, handle: lastHandle, y: Math.round(scrollY) });
  });
  const isReturn = () => {
    const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    return !!nav && nav.type === 'back_forward';
  };
  // Chrome restores its own scroll offset at load, after this script has run; when this document is a
  // return trip with a snapshot, that restoration is switched off and the card itself is the anchor.
  const snapFor = () => { const snap = isReturn() ? readSnap() : null; return snap && snap.search === location.search ? snap : null; };
  if (snapFor() && 'scrollRestoration' in history) history.scrollRestoration = 'manual';
  const restoreScroll = (root) => {
    const snap = snapFor();
    if (!snap) return;
    if (snap.handle) {
      const a = root.querySelector(`[data-akk-results] a[href*="/products/${snap.handle}"]`);
      const card = a && (a.closest('li') || a.closest('.akk-card'));
      if (card) { card.scrollIntoView({ block: 'center', ...instant }); return; }
    }
    if (typeof snap.y === 'number') window.scrollTo({ top: snap.y, left: 0, ...instant });
  };

  /* --- drawer --- */
  const openDrawer = (dialog) => {
    if (!dialog || typeof dialog.showModal !== 'function' || dialog.open) return;
    dialog.showModal();
    html.classList.add('akk-filter-open');
  };

  document.addEventListener('change', (event) => {
    const form = event.target.form;
    if (!form || !form.hasAttribute('data-akk-filters')) return;
    const root = rootOf(form);
    if (event.target.type === 'number') {
      // a price typed and left fires change; Enter fires submit a moment later: one fetch (C0, rank 4)
      if (pending) clearTimeout(pending);
      pending = setTimeout(() => { pending = null; load(root, formUrl(form)); }, 200);
      return;
    }
    load(root, formUrl(form));
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.hasAttribute || !form.hasAttribute('data-akk-filters')) return;
    event.preventDefault();
    load(rootOf(form), formUrl(form));
    const dialog = form.closest('dialog');
    if (dialog && dialog.open) dialog.close();
  });

  document.addEventListener('click', (event) => {
    const more = event.target.closest('[data-akk-more]');
    if (more) {
      event.preventDefault();
      append(rootOf(more), more);
      return;
    }
    const chip = event.target.closest('[data-akk-chip]');
    if (chip) {
      event.preventDefault();
      load(rootOf(chip), chip.href);
      return;
    }
    const opener = event.target.closest('[data-akk-filter-open]');
    if (opener) {
      openDrawer(document.getElementById(opener.getAttribute('aria-controls')));
      return;
    }
    const closer = event.target.closest('[data-akk-filter-close]');
    if (closer) {
      closer.closest('dialog').close();
      return;
    }
    const dialog = event.target.closest('dialog.akk-fdrawer');
    if (dialog && event.target === dialog) dialog.close();
  });

  /* --- the phone toolbar returns on scroll-up (C2, rank 13) --- */
  const stickyBar = (root) => {
    const bar = root.querySelector('.akk-coll__bar');
    if (!bar) return;
    let lastY = scrollY;
    const update = () => {
      const y = scrollY;
      const dy = y - lastY;
      if (Math.abs(dy) < 4) return;
      lastY = y;
      const sentinel = bar.previousElementSibling || root;
      const past = sentinel.getBoundingClientRect().bottom < 0;
      html.classList.toggle('akk-bar-up', past && dy < 0);
      html.classList.toggle('akk-bar-down', past && dy > 0);
    };
    window.addEventListener('scroll', update, { passive: true });
  };

  // A top-level word's own page: lift the sub-collections from its mega panel
  // in the rendered header (the panel's column headings), then drop the empty ones.
  const calm = (t) => (t === t.toLocaleUpperCase('is') && t !== t.toLocaleLowerCase('is')
    ? t.charAt(0) + t.slice(1).toLocaleLowerCase('is') : t);
  const path = (href) => { try { return new URL(href, location.href).pathname.replace(/\/$/, ''); } catch (e) { return ''; } };

  const liftNav = (root) => {
    const slot = root.querySelector('[data-akk-nav-slot]');
    const home = root.dataset.akkNavHome;
    if (!home || root.querySelector('.akk-coll__nav:not([data-akk-nav-slot])')) { if (slot) slot.remove(); return; }
    const item = Array.from(document.querySelectorAll('.akk-hdr__list > li')).find((li) => {
      const a = li.querySelector(':scope > .akk-hdr__link');
      return a && path(a.getAttribute('href')) === path(home);
    });
    // no header word for this collection: the reserved slot has nothing to hold (C0, rank 3)
    if (!item) { if (slot) slot.remove(); return; }
    const seen = new Set();
    const heads = Array.from(item.querySelectorAll('.akk-hdr__panel a.akk-hdr__colhead')).filter((a) => {
      const k = path(a.getAttribute('href'));
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
    if (!heads.length) { if (slot) slot.remove(); return; }
    const nav = slot || document.createElement('nav');
    nav.className = 'akk-coll__nav';
    nav.removeAttribute('data-akk-nav-slot');
    nav.removeAttribute('aria-hidden');
    nav.setAttribute('aria-label', root.dataset.akkNavLabel || '');
    const ul = document.createElement('ul');
    heads.forEach((a) => {
      const li = document.createElement('li');
      const link = document.createElement('a');
      link.href = a.getAttribute('href');
      link.textContent = calm(a.textContent.trim());
      li.appendChild(link);
      ul.appendChild(li);
      // products.json lists what the storefront really shows; the collection's
      // products_count also counts unlisted products.
      fetch(path(link.href) + '/products.json?limit=1', { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data && Array.isArray(data.products) && data.products.length === 0) li.remove();
          if (!ul.children.length) nav.remove();
        })
        .catch(() => {});
    });
    nav.appendChild(ul);
    if (!slot) {
      const bar = root.querySelector('.akk-coll__bar');
      if (bar) bar.parentNode.insertBefore(nav, bar);
    }
  };

  const first = document.querySelector('[data-akk-collection]');
  if (first) {
    liftNav(first);
    markImages(first);
    stickyBar(first);
    const settle = prependEarlier(first).catch(() => {}).then(() => { restoreScroll(first); arm(first); });
    window.addEventListener('pageshow', (event) => { if (!event.persisted) settle.then(() => restoreScroll(first)); });
  }

  document.addEventListener('close', (event) => {
    if (event.target.matches?.('dialog.akk-fdrawer')) html.classList.remove('akk-filter-open');
  }, true);
})();
